import {gsap} from 'gsap';

const {KitModule, registerModule} = window;

class Diagram extends KitModule {
  constructor($element) {
    super($element);

    this.setVars();
    this.bindUI();
    this.bindEvents();

    this.startTimer();
    this.render();
    this.initSquareAnimations();
  }

  setVars() {
    this.isDesktop = window.matchMedia('(min-width: 1000px)').matches;
    // Hold global DOM data depending on match media
    this.UI = {
      svg: this.isDesktop ? this.$squareSvg : this.$squareSvgMobile,
      topSquare: this.isDesktop ? this.$topSquare : this.$topSquareMobile,
      animationTarget: this.isDesktop ? this.$animationTarget : this.$animationTargetMobile,
      logoBar: this.isDesktop ? this.$logoBar : this.$logoBarMobile,
      squaresPath: this.isDesktop ? 'path:not([data-js-child="$logoBar"])' : 'path:not([data-js-child="$logoBarMobile"])',
    };

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)') === true || window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

    // We use the <path> element assigned to this.$animationTarget as the
    // position for all squares in the forward animation.
    const animationTargetBBox = this.UI.animationTarget.getBBox();
    this.animationTargetPos = {
      x: animationTargetBBox.x,
      y: animationTargetBBox.y,
      width: animationTargetBBox.width,
      height: animationTargetBBox.height,
      cx: animationTargetBBox.x + animationTargetBBox.width / 2,
      cy: animationTargetBBox.y + animationTargetBBox.height / 2,
    };

    this.currentIndex = 0;
    this.loopTimer = this.$element.dataset.loopTimer === 'true';
    this.timerDuration = parseInt(this.$element.style.getPropertyValue('--duration')) * 1000;

    const initialDuration = this.$element.style.getPropertyValue('--initial-duration');
    this.initialTimerDuration = initialDuration ? parseFloat(initialDuration) * 1000 : undefined;
    this.initialRun = this.initialTimerDuration ? true : false;

    this.forwardTimelines = [];
    this.reverseTimelines = [];
  }

  bindUI() {
    this.$squares = [...this.UI.svg.querySelectorAll(this.UI.squaresPath)];
  }

  bindEvents() {
    window.addEventListener('resize', this);
    this.$buttons.forEach(($button) => {
      $button.addEventListener('click', this.handleButtonClick.bind(this));
    });
  }

  startTimer() {
    const timerCallback = () => {
      this.currentIndex++;
      if (this.currentIndex >= this.$buttons.length) {
        if (this.loopTimer) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = this.$buttons.length - 1;
          this.stopTimer();
          return;
        }
      }

      this.render();
    };

    if (this.initialTimerDuration) {
      // If there's a separate timeout defined for the first animation, we need
      // to set that first and then set the repeating interval for there on out
      // once it fires.
      this.timer = setTimeout(() => {
        this.initialRun = false;
        timerCallback();

        this.timer = setInterval(timerCallback, this.timerDuration);
      }, this.initialTimerDuration);
    } else {
      // Otherwise, we can just set the interval right away.
      this.timer = setInterval(timerCallback, this.timerDuration);
    }
  }

  /**
   * Add appropriate classes and attributes to module elements,  depending on
   * current internal state.
   */
  render() {
    this.$element.dataset.currentIndex = this.currentIndex;
    this.$element.dataset.initialRun = this.initialRun;

    // Tabs
    this.$tabs.forEach(($tab, index) => {
      if (index === this.currentIndex && !this.initialRun) {
        $tab.classList.add('is-active');
      } else {
        $tab.classList.remove('is-active');
      }
    });

    // Buttons
    this.$buttons.forEach(($button, index) => {
      if (index === this.currentIndex) {
        $button.classList.add('is-active');

        if (this.timer && !this.initialRun) {
          $button.classList.add('show-timer');
        } else {
          $button.classList.remove('show-timer');
        }
      } else {
        $button.classList.remove('is-active', 'show-timer');
      }
    });

    // Animate Squares
    const direction = this.currentIndex === 1 ? 'forward' : 'reverse';
    this.animateSquares(direction);
  }

  /**
   * Since the forward animation isn't quite the same as the backward animation
   * (it skips the logo animation) we define two sets of timelines and play one
   * of them back depending on which direction is appropriate.
   */
  initSquareAnimations() {
    // initial state get directly rendered
    gsap.set(this.UI.animationTarget, {opacity: 0});
    gsap.set(this.UI.logoBar, {opacity: 0});
    // gsap.set(this.UI.lineSvg, {y: 50, opacity: 0});

    let didRegisterLogoBar = false;

    this.$squares.forEach((square) => {
      const box = square.getBBox();
      box.cx = box.x + box.width / 2;
      box.cy = box.y + box.height / 2;

      // All squares should move to the center of this.$animationTarget
      const x = (this.animationTargetPos.cx - box.cx);
      const y = (this.animationTargetPos.cy - box.cy);

      /*
       * Forward Animation
       */
      // init timeline and its default value
      const forwardTimeline = gsap.timeline({
        paused: true,
        defaults: {
          duration: 1,
          ease: 'sine.inOut',
        },
      });

      // move each square to target position calculated above
      forwardTimeline.to(square, {
        // Add a random fraction of a second so squares animate independently
        duration: this.prefersReducedMotion ? 0.01 : 2.5 + Math.random(),
        ease: 'power2.inOut',

        // If this is the "top square" (the one that appears on top of the pile)
        // change the fill to white.
        fill: square === this.UI.topSquare ? '#FFFFFF' : undefined,

        // Resize each square to match the this.$animationTarget
        scale: this.animationTargetPos.width / box.width,
        transformOrigin: 'center',
        x: x,
        y: y,
      });

      // increase the scale of the top-most square (leaving all others as-is)
      forwardTimeline.to(square, {
        ease: 'expo.out',
        delay: this.prefersReducedMotion ? 0.01 : 0.6,
        duration: this.prefersReducedMotion ? 0.01 : 1.25,
        scale: square === this.UI.topSquare ? 1.95 : 1,
      });

      // Move the logo bar into place
      if (!didRegisterLogoBar) {
        forwardTimeline.to(this.UI.logoBar, {
          duration: this.prefersReducedMotion ? 0.01 : 1.25,
          opacity: 1,
          y: -70,
        }, '-=1.5');

        didRegisterLogoBar = true;
      }

      this.forwardTimelines.push(forwardTimeline);

      /*
       * Reverse Animation
       */
      const reverseTimeline = gsap.timeline({
        paused: true,
        defaults: {
          duration: this.prefersReducedMotion ? 0.01 : 1,
          ease: 'sine.inOut',
        },
      });

      // Don't animate the logo bar, just immediately hide it
      reverseTimeline.set(this.UI.logoBar, {
        opacity: 0,
      });

      // Move squares back to their original location
      reverseTimeline.to(square, {
        // Add a random fraction of a second so squares animate independently
        duration: this.prefersReducedMotion ? 0.01 : 2 + Math.random(),
        ease: 'expo.out',

        // Return the top square to its original fill color
        fill: square === this.UI.topSquare ? square.getAttribute('fill') : undefined,
        scale: 1,
        transformOrigin: 'center',
        x: 0,
        y: 0,
      });

      this.reverseTimelines.push(reverseTimeline);
    });
  }

  onresize() {
    this.isDesktop = window.matchMedia('(min-width: 1000px)').matches;
  }

  /**
   * @param {'forward'|'reverse'} direction
   */
  animateSquares(direction) {
    this.forwardTimelines.forEach((timeline) => {
      if (direction === 'forward') {
        if (!timeline.isActive()) {
          timeline.restart();
          timeline.play();
        }
      } else {
        if (timeline.isActive()) {
          timeline.pause();
        }
      }
    });

    this.reverseTimelines.forEach((timeline) => {
      if (direction === 'reverse') {
        if (!timeline.isActive()) {
          timeline.restart();
          timeline.play();
        }
      } else {
        if (timeline.isActive()) {
          timeline.pause();
        }
      }
    });
  }

  /**
   * Disable timer and set the new current index
   * @param {Event} event
   */
  handleButtonClick(event) {
    this.stopTimer();

    const originalIndex = this.currentIndex;

    this.$buttons.forEach(($button, index) => {
      if ($button === event.target) {
        this.currentIndex = index;
      }
    });

    if (originalIndex !== this.currentIndex) {
      this.render();
    }
  }

  stopTimer() {
    clearInterval(this.timer);
    delete this.timer;
  }
}

registerModule('Diagram', Diagram);
